import { useState, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  Alert,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import { getUsageReportData } from '../api/dashboard';
import { generatePDF, generateImage } from '../utils/pdfGenerator';
import UsageReport from './UsageReport';
import type { FilterOption, UsageReportData } from '../types';
import { formatOrgDisplayName } from '../utils/display';

interface GenerateReportDialogProps {
  open: boolean;
  onClose: () => void;
  availableOrgs: FilterOption[];
  dateRange: {
    start: Date;
    end: Date;
  };
  excludeUserIds?: string[];
}

export default function GenerateReportDialog({
  open,
  onClose,
  availableOrgs,
  dateRange,
  excludeUserIds,
}: GenerateReportDialogProps) {
  const [selectedOrg, setSelectedOrg] = useState<FilterOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [reportData, setReportData] = useState<UsageReportData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reportRef = useRef<HTMLDivElement>(null);

  const handleOrgChange = useCallback(async (org: FilterOption | null) => {
    setSelectedOrg(org);
    setReportData(null);
    setError(null);

    if (!org) return;

    setIsLoading(true);
    try {
      const data = await getUsageReportData(org.id, dateRange.start, dateRange.end, excludeUserIds);
      setReportData(data);
    } catch (err) {
      console.error('Failed to fetch report data:', err);
      setError('Failed to load report data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, excludeUserIds]);

  const handleGeneratePDF = useCallback(async () => {
    if (!reportRef.current || !reportData) return;

    setIsGenerating(true);
    try {
      const filename = `Pelles_Usage_Report_${reportData.orgName.replace(/[^a-zA-Z0-9]/g, '_')}_${reportData.periodStart}_to_${reportData.periodEnd}.pdf`;
      await generatePDF(reportRef.current, filename);
    } catch (err) {
      console.error('Failed to generate PDF:', err);
      setError('Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [reportData]);

  const handleGenerateImage = useCallback(async () => {
    if (!reportRef.current || !reportData) return;

    setIsGenerating(true);
    try {
      const filename = `Pelles_Usage_Report_${reportData.orgName.replace(/[^a-zA-Z0-9]/g, '_')}_${reportData.periodStart}_to_${reportData.periodEnd}.png`;
      await generateImage(reportRef.current, filename, 'png');
    } catch (err) {
      console.error('Failed to generate image:', err);
      setError('Failed to generate image. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [reportData]);

  const handleClose = useCallback(() => {
    setSelectedOrg(null);
    setReportData(null);
    setError(null);
    onClose();
  }, [onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '95vh',
          bgcolor: '#0f172a',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PictureAsPdfIcon color="primary" />
        Generate Usage Report
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select an organization to generate a usage report for the current date range.
          </Typography>

          <Autocomplete
            size="small"
            options={availableOrgs}
            getOptionLabel={(option: FilterOption) => formatOrgDisplayName(option.name)}
            value={selectedOrg}
            onChange={(_, newValue) => handleOrgChange(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Organization"
                placeholder="Choose an organization..."
              />
            )}
            sx={{ maxWidth: 400 }}
          />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {reportData && !isLoading && (
          <Box
            sx={{
              overflow: 'auto',
              maxHeight: 'calc(95vh - 300px)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ transform: 'scale(0.7)', transformOrigin: 'top center', flexShrink: 0 }}>
              <UsageReport ref={reportRef} data={reportData} />
            </Box>
          </Box>
        )}

        {!selectedOrg && !isLoading && (
          <Box
            sx={{
              py: 8,
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography>Select an organization to preview the report</Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleGenerateImage}
          variant="outlined"
          disabled={!reportData || isGenerating}
          startIcon={isGenerating ? <CircularProgress size={16} /> : <ImageIcon />}
        >
          {isGenerating ? 'Generating...' : 'Download PNG'}
        </Button>
        <Button
          onClick={handleGeneratePDF}
          variant="contained"
          disabled={!reportData || isGenerating}
          startIcon={isGenerating ? <CircularProgress size={16} /> : <PictureAsPdfIcon />}
        >
          {isGenerating ? 'Generating...' : 'Download PDF'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
